import { useEffect, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore, useOnboardingStore } from './stores';
import { Layout, ProtectedRoute } from './components/layout';
import {
  LoginPage,
  RegisterPage,
  EntriesPage,
  EntryEditPage,
  EntryViewPage,
  SettingsPage,
  MagicLinkVerifyPage,
  OnboardingPage,
  MapPage,
  TodayPage,
} from './pages';

function LoadingSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
    </div>
  );
}

function AppRoutes() {
  const { isAuthenticated, isLoading: authLoading, refreshAuth } = useAuthStore();
  const { status, isLoading: onboardingLoading, fetchStatus } = useOnboardingStore();

  useEffect(() => {
    fetchStatus();
    refreshAuth();
  }, [fetchStatus, refreshAuth]);

  if (authLoading || onboardingLoading) {
    return <LoadingSpinner />;
  }

  // If onboarding is not complete, redirect to onboarding
  if (status && !status.isComplete) {
    return (
      <Routes>
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="*" element={<Navigate to="/onboarding" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        {/* Public routes */}
        <Route
          path="login"
          element={
            isAuthenticated ? (
              <Navigate to="/entries" replace />
            ) : (
              <LoginPage />
            )
          }
        />
        <Route
          path="register"
          element={
            isAuthenticated ? (
              <Navigate to="/entries" replace />
            ) : (
              <RegisterPage />
            )
          }
        />
        <Route
          path="auth/magic-link"
          element={
            isAuthenticated ? (
              <Navigate to="/entries" replace />
            ) : (
              <MagicLinkVerifyPage />
            )
          }
        />
        <Route path="onboarding" element={<OnboardingPage />} />

        {/* Protected routes */}
        <Route
          index
          element={
            isAuthenticated ? (
              <Navigate to="/entries" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="entries"
          element={
            <ProtectedRoute>
              <EntriesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="entries/new"
          element={
            <ProtectedRoute>
              <EntryEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="entries/:id/view"
          element={
            <ProtectedRoute>
              <EntryViewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="entries/:id/edit"
          element={
            <ProtectedRoute>
              <EntryEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="map"
          element={
            <ProtectedRoute>
              <MapPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="today"
          element={
            <ProtectedRoute>
              <TodayPage />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <AppRoutes />
    </Suspense>
  );
}
